import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { planService } from '@/services/planService'
import { useToast } from '@/contexts/ToastContext'
import { Plan } from '@/types/plan'

interface CreatePlanModalProps {
  isOpen: boolean
  relationshipId: string
  menteeId: string
  onClose: () => void
  onPlanCreated: (plan: Plan) => void
}

const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  isOpen,
  relationshipId,
  menteeId,
  onClose,
  onPlanCreated
}) => {
  const { showToast } = useToast()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) {
    return null
  }

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {}
    if (!title.trim()) {
      nextErrors.title = 'Title is required'
    }
    if (!startDate) {
      nextErrors.startDate = 'Start date is required'
    }
    if (!endDate) {
      nextErrors.endDate = 'End date is required'
    }
    if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
      nextErrors.endDate = 'Start date must be before end date'
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validate()) {
      return
    }

    try {
      setIsSubmitting(true)
      const plan = await planService.createPlan({
        relationshipId,
        menteeId,
        title: title.trim(),
        description: description.trim(),
        startDate,
        endDate
      })
      showToast({ type: 'success', message: 'Plan created as draft' })
      onPlanCreated(plan)
      onClose()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to create plan'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">Create Plan</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="plan-title" className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              id="plan-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. Frontend fundamentals"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          <div>
            <label htmlFor="plan-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="plan-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="What should this plan achieve?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="plan-start-date" className="block text-sm font-medium text-gray-700 mb-1">
                Start date *
              </label>
              <input
                id="plan-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
            </div>
            <div>
              <label htmlFor="plan-end-date" className="block text-sm font-medium text-gray-700 mb-1">
                End date *
              </label>
              <input
                id="plan-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create Plan'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreatePlanModal
